import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useState } from "react";
import { z } from "zod";

import { getBlastroInstallationStart } from "@/api/blastroInstallations";
import { SITE_EMPTY_NAME } from "@/constants/uiConstants";
import { useAuth } from "@/contexts/AuthContext";
import { isBlastroEnabledForSite } from "@/features/Blastro/installationLink";
import { useIsDemoUser } from "@/utils/demoMode";

const searchSchema = z
  .object({
    shop: z
      .string()
      .max(255)
      .regex(/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/)
      .refine((shop) => shop === shop.trim()),
  })
  .strict();

export const Route = createFileRoute("/blastro/connect")({
  validateSearch: zodValidator(searchSchema),
  beforeLoad: ({ context, search, location }) => {
    if (location.hash) throw new Error("Invalid Blastro connection link");
    if (!context.auth?.isAuthenticated)
      throw redirect({
        to: "/login",
        search: {
          redirect: `/blastro/connect?shop=${encodeURIComponent(search.shop)}`,
        },
      });
  },
  errorComponent: () => (
    <Alert severity="warning">
      Open Blastro in Shopify, then choose Open Queen One to connect your store.
    </Alert>
  ),
  component: RouteComponent,
});

function RouteComponent() {
  const { shop } = Route.useSearch();
  return <BlastroShopConnectPage key={shop} shop={shop} />;
}

function BlastroShopConnectPage({ shop }: { shop: string }) {
  const { sites, isAuthenticated } = useAuth();
  const isDemoUser = useIsDemoUser();
  const [siteId, setSiteId] = useState("");
  const eligibleSites = sites.filter((site) =>
    isBlastroEnabledForSite(site.id),
  );
  const selectedSite = eligibleSites.find((site) => site.id === siteId);
  const start = useMutation({
    mutationFn: (selectedSiteId: string) =>
      getBlastroInstallationStart(selectedSiteId, shop),
    retry: false,
  });
  const target =
    selectedSite && start.data?.siteId === selectedSite.id
      ? start.data
      : undefined;

  return (
    <Box sx={{ display: "flex", justifyContent: "center", p: 7 }}>
      <Card variant="outlined" sx={{ width: "100%", maxWidth: 520 }}>
        <CardContent sx={{ p: 7 }}>
          <Stack spacing={5}>
            <Typography component="h1" variant="h4">
              Connect Blastro
            </Typography>
            {!isAuthenticated || isDemoUser || eligibleSites.length === 0 ? (
              <Alert severity="warning">
                You do not have permission to connect Blastro to an available
                brand. Sign in with an account that has access to your brand.
              </Alert>
            ) : (
              <>
                <Typography sx={{ overflowWrap: "anywhere" }}>
                  Shopify store: <strong>{shop}</strong>
                </Typography>
                <TextField
                  select
                  label="Queen One brand"
                  value={siteId}
                  onChange={(event) => {
                    setSiteId(event.target.value);
                    start.reset();
                  }}
                  disabled={start.isPending}
                  fullWidth
                >
                  {eligibleSites.map((site) => (
                    <MenuItem key={site.id} value={site.id}>
                      {site.name.trim() || SITE_EMPTY_NAME}
                    </MenuItem>
                  ))}
                </TextField>
                <Typography>
                  Choose your brand, then confirm store access in Shopify. You
                  will review the connection before it is saved. Connecting does
                  not turn on Blastro automation.
                </Typography>
                {start.isError && (
                  <Alert severity="warning">
                    This connection could not be verified. Check your brand
                    access or start again from Blastro in Shopify.
                  </Alert>
                )}
                {target ? (
                  <Button
                    variant="contained"
                    href={target.shopifyAdminUrl ?? target.connectUrl}
                    target="_top"
                    rel="noreferrer"
                  >
                    Continue to Shopify
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    loading={start.isPending}
                    disabled={!selectedSite || start.isPending}
                    onClick={() => {
                      if (selectedSite && !start.isPending)
                        start.mutate(selectedSite.id);
                    }}
                  >
                    Check connection
                  </Button>
                )}
              </>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
